import React from 'react';
import { useTranslation } from 'react-i18next';
import { Reorder } from 'motion/react';

import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import Answer from './Answer';
import Iconify from '../../../../theme/iconify/iconify';
import { questionTypeList } from '../../../util/form-values';

function Question({ question, final, update, index, last, move, duplicate, remove }) {
    const [q, setQ] = React.useState('');
    const [answers, setAnswers] = React.useState(question?.answers.map((answer) => answer.text));
    const [tempAnswers, setTempAnswers] = React.useState(question?.answers.map((answer) => answer.text));
    const [correct, setCorrect] = React.useState(question?.answers.map((answer) => answer.is_correct));
    const [required, setRequired] = React.useState(null);

    const { t } = useTranslation();

    const reorder = () => {
        setAnswers(tempAnswers);
        setCorrect(tempAnswers.map((answer) => correct[answers.indexOf(answer)]));
    }

    React.useEffect(() => {
        if (!q) return;

        const data = {
            id: question?.id,
            type: question?.type,
            question: q,
            answers: answers.map((answer, i) => ({
                id: question?.answers.find((a) => a.text === answer)?.id,
                text: answer,
                is_correct: correct[i]
            })),
            is_required: required
        };
        update(index, data);
    }, [answers, correct, required]);

    const insertAnswer = () => {
        setAnswers([...answers, '']);
        setCorrect([...correct, false]);
    }

    const handleBlur = () => {
        if (!q) return;

        const data = {
            id: question?.id,
            type: question?.type,
            question: q,
            answers: answers.map((answer, i) => ({ id: question?.answers[i]?.id, text: answer, is_correct: correct[i] })),
            is_required: required
        };
        update(index, data);
    }

    React.useEffect(() => {
        if (q) return;

        setQ(question?.question);
        if (required === null) setRequired(question?.is_required);
    }, [question]);

    return (
        <>
            <Stack sx={{ border: '1px solid rgba(33, 43, 54, 0.12)', borderRadius: '8px' }}>
                <Grid container sx={{ backgroundColor: 'rgba(33, 43, 54, 0.04)', borderRadius: '8px 8px 0px 0px', p: 0.5, borderBottom: '1px solid rgba(33, 43, 54, 0.12)' }}>
                    <Grid item xs={12}>
                        <Grid container alignItems='center'>
                            <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start' }}>
                                <Typography variant='h6' sx={{ fontSize: '0.9rem !important', textAlign: 'center', ml: 2, mr: 1.25 }}>
                                    {t('course.modals.newQuiz.questions.question')} {index + 1}
                                </Typography>
                                <Divider orientation='vertical' flexItem sx={{ my: 0.5, mx: 1 }} />
                                <Tooltip title={t('course.modals.newQuiz.questions.tooltips.questionType')} arrow>
                                    <IconButton
                                        sx={{
                                            borderRadius: '8px',
                                            p: 0.5,
                                            px: 1,
                                            '&:hover': {
                                                backgroundColor: 'rgba(145, 158, 171, 0.08)',
                                            },
                                        }}
                                        disableFocusRipple
                                        disableRipple
                                    >
                                        <Iconify icon={questionTypeList?.filter((qt) => qt.type === question?.type)[0].icon} width={18} sx={{ color: 'primary.main' }} />
                                        <Typography variant='body2' sx={{ ml: 0.5, color: 'text.primary', fontSize: '0.8rem' }}>
                                            {t(`course.modals.newQuiz.questions.type.${question?.type}`)}
                                        </Typography>
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                            <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'right', p: 1 }}>
                                <Tooltip title={t('course.modals.newQuiz.questions.tooltips.moveToStart')} arrow disabled={index === 0}>
                                    <span style={{ display: 'inline-flex' }}>
                                        <IconButton
                                            disabled={index === 0}
                                            onClick={() => move(index, 'start')}
                                            sx={{
                                                pt: '4px',
                                                pb: '4px',
                                                pr: '4px',
                                                pl: '4px',
                                                borderRadius: '8px'
                                            }}
                                        >
                                            <Iconify icon='octicon:move-to-start-16' />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title={t('course.modals.newQuiz.questions.tooltips.moveUp')} arrow disabled={index === 0}>
                                    <span style={{ display: 'inline-flex' }}>
                                        <IconButton
                                            disabled={index === 0}
                                            onClick={() => move(index, 'up')}
                                            sx={{
                                                pt: '4px',
                                                pb: '4px',
                                                pr: '4px',
                                                pl: '4px',
                                                borderRadius: '8px'
                                            }}
                                        >
                                            <Iconify icon='stash:arrow-up-solid' />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title={t('course.modals.newQuiz.questions.tooltips.moveDown')} arrow disabled={last}>
                                    <span style={{ display: 'inline-flex' }}>
                                        <IconButton
                                            disabled={last}
                                            onClick={() => move(index, 'down')}
                                            sx={{
                                                pt: '4px',
                                                pb: '4px',
                                                pr: '4px',
                                                pl: '4px',
                                                borderRadius: '8px'
                                            }}
                                        >
                                            <Iconify icon='stash:arrow-down-solid' />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title={t('course.modals.newQuiz.questions.tooltips.moveToEnd')} arrow disabled={last}>
                                    <span style={{ display: 'inline-flex' }}>
                                        <IconButton
                                            disabled={last}
                                            onClick={() => move(index, 'end')}
                                            sx={{
                                                pt: '4px',
                                                pb: '4px',
                                                pr: '4px',
                                                pl: '4px',
                                                borderRadius: '8px'
                                            }}
                                        >
                                            <Iconify icon='octicon:move-to-end-16' />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Divider orientation='vertical' flexItem sx={{ m: '4px', mx: 1.5 }} />
                                <Tooltip title={t('course.modals.newQuiz.questions.tooltips.cloneQuestion')} arrow>
                                    <IconButton
                                        onClick={() => duplicate(index)}
                                        sx={{
                                            pt: '4px',
                                            pb: '4px',
                                            pr: '6px',
                                            pl: '6px',
                                            borderRadius: '8px'
                                        }}
                                    >
                                        <Iconify icon='bx:copy' width={18} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={t('course.modals.newQuiz.questions.tooltips.removeQuestion')} arrow>
                                    <IconButton
                                        color='error'
                                        onClick={() => remove(index)}
                                        sx={{
                                            pt: '4px',
                                            pb: '4px',
                                            pr: '6px',
                                            pl: '6px',
                                            borderRadius: '8px'
                                        }}
                                    >
                                        <Iconify icon='eva:trash-outline' width={17} />
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid container sx={{ px: '16px', pt: '8px', pb: '4px', mt: 1 }}>
                    <Grid item xs={12}>
                        <TextField
                            variant='outlined'
                            label={t('course.modals.newQuiz.questions.inputs.question.label')}
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            onBlur={handleBlur}
                            InputLabelProps={{ shrink: q?.length > 0 }}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Reorder.Group
                            className='mISLec_course_answers'
                            axis='y'
                            values={answers}
                            onReorder={(e) => { setTempAnswers(e); }}
                        >
                            {answers.map((answer, index) => (
                                <Answer key={index} type={question?.type} answer={answer} index={index} answers={answers} setAnswers={setAnswers} correct={correct} setCorrect={setCorrect} reorder={reorder} />
                            ))}
                        </Reorder.Group>
                    </Grid>
                </Grid>
                <Grid container sx={{ px: '16px', pb: '8px', fontSize: '0.8rem' }}>
                    <Grid item xs={12} md={6}>
                        <Tooltip title={t('course.modals.newQuiz.questions.tooltips.insertAnswer')} arrow>
                            <Grid
                                container
                                wrap='nowrap'
                                alignItems='center'
                                onClick={() => insertAnswer()}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    p: 0.5,
                                    fontSize: '0.8rem',
                                    width: 'auto',
                                    '&:hover': {
                                        backgroundColor: 'rgba(145, 158, 171, 0.08)',
                                        borderRadius: '8px'
                                    }
                                }}
                            >
                                <Grid item sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                                    <Iconify icon='stash:list-add' sx={{ color: 'primary.main', lineHeight: 1, verticalAlign: 'middle' }} width={18} />
                                </Grid>
                                <Grid item>
                                    <Typography variant='caption' color='text.secondary'>
                                        {t('course.modals.newQuiz.questions.buttons.insertAnswer')}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Tooltip>
                    </Grid>
                    {final &&
                        <Grid item xs={12} md={6}>
                            <Grid container justifyContent='flex-start' alignItems='center' flexDirection='row-reverse'>
                                <Tooltip title={t(`course.modals.newQuiz.questions.tooltips.required`)} arrow>
                                    <Grid item onClick={() => setRequired(!required)} sx={{ cursor: 'pointer', display: 'flex', justifyContent: 'end', p: 0.5, '&:hover': { backgroundColor: 'rgba(145, 158, 171, 0.08)', borderRadius: '8px' } }}>
                                        <Iconify icon={required ? 'mingcute:checkbox-line' : 'fluent:checkbox-unchecked-24-filled'} width={18} sx={{ color: 'primary.main', mr: 0.5 }} />
                                        {t('course.modals.newQuiz.questions.inputs.required.label')}
                                    </Grid>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    }
                </Grid>
            </Stack>
        </>
    );
}

export default Question;